import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { ProjectService } from "../services/projectService";
import { TakeoffService } from "../services/TakeoffService";

// Create the context
const DataContext = createContext();

// Custom hook to use the data context
export const useDataContext = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error("useDataContext must be used within a DataProvider");
  }
  return context;
};

// Provider component
export const DataProvider = ({ children }) => {
  // Core data states
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientsWithBilling, setClientsWithBilling] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [takeoffs, setTakeoffs] = useState([]);

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetchTime, setLastFetchTime] = useState(null);

  // Track if data has been loaded at least once
  const [dataInitialized, setDataInitialized] = useState(false);

  // Load all data
  const loadAllData = useCallback(
    async (forceRefresh = false) => {
      // Cache duration in milliseconds (5 minutes)
      const CACHE_DURATION = 5 * 60 * 1000;

      // Check if we should use cached data
      if (!forceRefresh && dataInitialized && lastFetchTime) {
        const timeSinceLastFetch = Date.now() - lastFetchTime;
        if (timeSinceLastFetch < CACHE_DURATION) {
          console.log("📊 [DataContext] Using cached data");
          return;
        }
      }

      console.log("📊 [DataContext] Loading all data...");

      try {
        console.log("🔍 [DataContext] Setting loading to TRUE");
        setLoading(true);
        setError(null);

        console.log("📊 [DataContext] Starting parallel data fetching...");
        const startTime = Date.now();

        const [
          allProjects,
          clientsData,
          clientsBillingData,
          contactsData,
          employeesData,
          takeoffData,
        ] = await Promise.all([
          ProjectService.getAllProjects(),
          ProjectService.getClients(),
          ProjectService.getClientsWithBillingInfo(),
          ProjectService.getContacts(),
          ProjectService.getEmployees(),
          TakeoffService.getAllTakeoffs(),
        ]);

        const fetchTime = Date.now() - startTime;
        console.log(
          `📊 [DataContext] Data fetching completed in ${fetchTime}ms`,
        );

        console.log("📊 [DataContext] Data loaded successfully:");
        console.log("  - Projects:", allProjects?.length || 0);
        console.log("  - Clients:", clientsData?.length || 0);
        console.log(
          "  - Clients with billing:",
          clientsBillingData?.length || 0,
        );
        console.log("  - Contacts:", contactsData?.length || 0);
        console.log("  - Employees:", employeesData?.length || 0);
        console.log("  - Takeoffs:", takeoffData?.length || 0);

        setProjects(allProjects || []);
        setClients(clientsData || []);
        setClientsWithBilling(clientsBillingData || []);
        setContacts(contactsData || []);
        setEmployees(employeesData || []);
        setTakeoffs(takeoffData || []);

        setLastFetchTime(Date.now());
        setDataInitialized(true);
        console.log("🔍 [DataContext] Setting loading to FALSE - SUCCESS");
      } catch (err) {
        console.error("❌ [DataContext] Error loading data:", err);
        setError(err.message);
        console.log("🔍 [DataContext] Setting loading to FALSE - ERROR");
      } finally {
        setLoading(false);
        console.log("🔍 [DataContext] FINALLY block - loading set to FALSE");
      }
    },
    [dataInitialized, lastFetchTime],
  );

  // Refresh specific data types
  const refreshProjects = useCallback(async () => {
    console.log("🔄 [DataContext] Refreshing projects...");
    try {
      const allProjects = await ProjectService.getAllProjects();
      setProjects(allProjects || []);
    } catch (err) {
      console.error("❌ [DataContext] Error refreshing projects:", err);
    }
  }, []);

  const refreshClients = useCallback(async () => {
    console.log("🔄 [DataContext] Refreshing clients...");
    try {
      const [clientsData, clientsBillingData] = await Promise.all([
        ProjectService.getClients(),
        ProjectService.getClientsWithBillingInfo(),
      ]);
      setClients(clientsData || []);
      setClientsWithBilling(clientsBillingData || []);
    } catch (err) {
      console.error("❌ [DataContext] Error refreshing clients:", err);
    }
  }, []);

  const refreshTakeoffs = useCallback(async () => {
    console.log("🔄 [DataContext] Refreshing takeoffs...");
    try {
      const takeoffData = await TakeoffService.getAllTakeoffs();
      setTakeoffs(takeoffData || []);
    } catch (err) {
      console.error("❌ [DataContext] Error refreshing takeoffs:", err);
    }
  }, []);

  // Load data on mount only if not already loaded
  useEffect(() => {
    if (!dataInitialized) {
      loadAllData();
    }
  }, [dataInitialized, loadAllData]);

  // Update a single project
  const updateProject = useCallback(
    async (projectId, updates) => {
      try {
        await ProjectService.updateProject(projectId, updates);
        // Refresh projects after update
        await refreshProjects();
      } catch (err) {
        console.error("❌ [DataContext] Error updating project:", err);
        throw err;
      }
    },
    [refreshProjects],
  );

  // Update a single client
  const updateClient = useCallback(
    async (clientId, updates) => {
      try {
        await ProjectService.updateClient(clientId, updates);
        // Refresh clients after update
        await refreshClients();
      } catch (err) {
        console.error("❌ [DataContext] Error updating client:", err);
        throw err;
      }
    },
    [refreshClients],
  );

  const value = {
    // Data
    projects,
    clients,
    clientsWithBilling,
    contacts,
    employees,
    takeoffs,

    // States
    loading,
    error,
    dataInitialized,

    // Actions
    loadAllData,
    refreshProjects,
    refreshClients,
    refreshTakeoffs,
    updateProject,
    updateClient,
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};
